import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AddCircleOutline, Edit } from "@mui/icons-material";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import LeaveViewController from "../../redux/actions/leaveApiAction";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ExportButton from "../../components/ag grid/ExportButton";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ListPageLayout from "../../components/layout/ListPageLayout";
import AddLeaveBalanceFormModal from "./AddLeaveBalance";
import LeaveBalanceMobileView from "./LeaveBalanceMobileView";
import { toolbarButtonSx } from "../../components/auth/authTheme";

import "../../styles/styles.css";
import "../../styles/layout.css";

const LeaveBalanceList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { getLeaveBalance } = LeaveViewController();
  const { leaveBalance } = useSelector((state) => state.leave);

  const [showModal, setShowModal] = useState(false);
  const [editLeaveBalance, setEditLeaveBalance] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(getLeaveBalance());
  }, [dispatch]);

  const generateLeaveYears = () => {
    const currentYear = new Date().getFullYear();
    const fromData = (leaveBalance || [])
      .map((b) => b.leave_year)
      .filter(Boolean);
    const years = [];
    for (let year = currentYear + 1; year >= 2020; year -= 1) {
      years.push(year);
    }
    return Array.from(new Set([...years, ...fromData])).sort((a, b) => b - a);
  };

  const yearOptions = useMemo(() => generateLeaveYears(), [leaveBalance]);

  const filteredData = useMemo(
    () =>
      (leaveBalance || []).filter(
        (balance) => Number(balance.leave_year) === Number(selectedYear)
      ),
    [leaveBalance, selectedYear]
  );

  const openModalForCreate = useCallback(() => {
    setEditLeaveBalance(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditLeaveBalance(row);
    setShowModal(true);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR No.",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Emp ID",
        valueGetter: (params) => params.data.employee?.employee_id || "N/A",
        width: 100,
        maxWidth: 110,
        minWidth: 100,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee",
        valueGetter: (params) =>
          `${params.data.employee?.first_name || ""} ${
            params.data.employee?.last_name || ""
          }`.trim(),
        flex: 1,
        minWidth: 160,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Leave Year",
        field: "leave_year",
        width: 100,
        maxWidth: 110,
        minWidth: 100,
        suppressSizeToFit: true,
      },
      {
        headerName: "Paid Balance",
        valueGetter: (params) =>
          `${params.data.remaining_paid_leave_balance ?? 0} / ${params.data.paid_leave_balance ?? 0}`,
        flex: 1,
        minWidth: 130,
      },
      {
        headerName: "Unpaid Balance",
        valueGetter: (params) =>
          `${params.data.remaining_unpaid_leave_balance ?? 0} / ${params.data.unpaid_leave_balance ?? 0}`,
        flex: 1,
        minWidth: 130,
      },
      {
        headerName: "Carry Forward",
        field: "carry_forward_days",
        width: 120,
        maxWidth: 130,
        minWidth: 120,
        suppressSizeToFit: true,
      },
      {
        headerName: "Total Balance",
        valueGetter: (params) =>
          `${params.data.remaining_total_leave_balance ?? 0} / ${params.data.total_leave_balance ?? 0}`,
        flex: 1,
        minWidth: 130,
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(params.data)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleEdit]
  );

  const yearFilter = (
    <FormControl size="small" sx={{ minWidth: 110 }}>
      <InputLabel id="leave-balance-year-label">Year</InputLabel>
      <Select
        labelId="leave-balance-year-label"
        value={selectedYear}
        label="Year"
        onChange={(e) => setSelectedYear(Number(e.target.value))}
      >
        {yearOptions.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <>
      {isMobile ? (
        <LeaveBalanceMobileView
          records={filteredData}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          yearOptions={yearOptions}
          onEdit={handleEdit}
          onAdd={openModalForCreate}
        />
      ) : (
        <ListPageLayout
          title="Leave Balance"
          subtitle="View and manage employee leave allocations by year"
          headerAction={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutline />}
              onClick={openModalForCreate}
              sx={toolbarButtonSx}
            >
              Manage Leave Balance
            </Button>
          }
          toolbarRight={
            <>
              <ExportButton
                gridRef={gridRef}
                fileName={`leave_balance_${selectedYear}.xlsx`}
                exportColumns={[
                  "employee.employee_id",
                  "leave_year",
                  "remaining_paid_leave_balance",
                  "paid_leave_balance",
                  "remaining_unpaid_leave_balance",
                  "unpaid_leave_balance",
                  "carry_forward_days",
                  "remaining_total_leave_balance",
                  "total_leave_balance",
                ]}
                disabled={selectedRows.length === 0}
              />
              {yearFilter}
              <PageSizeSelector
                pageSize={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              />
            </>
          }
        >
          <div className="list-page-grid">
            <ReusableGrid
              rowData={filteredData}
              columnDefs={columnDefs}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              getRowId={(params) => String(params.data.id)}
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No leave balances found"
              emptyHint={`No records for ${selectedYear}. Click "Manage Leave Balance" to add one.`}
            />
          </div>
        </ListPageLayout>
      )}

      {showModal && (
        <AddLeaveBalanceFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editLeaveBalance}
        />
      )}
    </>
  );
};

export default LeaveBalanceList;
